import { useId, type ReactElement } from 'react';
import { Button } from '@/components/atoms/button';
import { FormInput } from '@/components/molecules/form-input';
import { Micro } from '@/components/atoms/micro';
import {
  CONTRIBUTE_SOURCE_KINDS,
  SOURCE_NOTE_MAX_LENGTH,
  SOURCE_VALUE_MAX_LENGTH,
  type ContributeSourceDraft,
  type ContributeSourceKind,
} from '@/lib/contribute-form';

const KIND_LABEL: Record<ContributeSourceKind, string> = {
  url: 'URL',
  book: 'Book',
  recording: 'Recording',
  youtube: 'YouTube',
  spotify: 'Spotify',
};

const KIND_PLACEHOLDER: Record<ContributeSourceKind, string> = {
  url: 'https://...',
  book: 'Book title — author, year, page',
  recording: 'Album / track, artist, year',
  youtube: 'https://youtube.com/...',
  spotify: 'https://open.spotify.com/...',
};

const URL_KINDS = new Set<ContributeSourceKind>(['url', 'youtube', 'spotify']);

const MALFORMED_URL_HINT = "URL doesn't look right — submit anyway, an editor will check.";
const EMPTY_STATE_COPY = 'No sources yet — add one below if you have it.';

function isPossiblyValidUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

export type ContributeSourcesSectionProps = {
  sources: ContributeSourceDraft[];
  onAddSource: (kind: ContributeSourceKind) => void;
  onRemoveSource: (draftId: string) => void;
  onUpdateSource: (
    draftId: string,
    patch: Partial<Pick<ContributeSourceDraft, 'value' | 'note'>>,
  ) => void;
};

export function ContributeSourcesSection({
  sources,
  onAddSource,
  onRemoveSource,
  onUpdateSource,
}: ContributeSourcesSectionProps): ReactElement {
  return (
    <div className="flex flex-col gap-4">
      {sources.length === 0 && (
        <p className="text-sm text-[var(--ink-3)]">{EMPTY_STATE_COPY}</p>
      )}

      {sources.map((source, index) => (
        <SourceEntry
          key={source.draftId}
          source={source}
          index={index}
          onRemoveSource={onRemoveSource}
          onUpdateSource={onUpdateSource}
        />
      ))}

      <div className="flex flex-wrap gap-2">
        {CONTRIBUTE_SOURCE_KINDS.map((kind) => (
          <Button
            key={kind}
            variant="ghost"
            type="button"
            onClick={() => onAddSource(kind)}
          >
            Add {KIND_LABEL[kind]}
          </Button>
        ))}
      </div>
    </div>
  );
}

type SourceEntryProps = {
  source: ContributeSourceDraft;
  index: number;
  onRemoveSource: (draftId: string) => void;
  onUpdateSource: ContributeSourcesSectionProps['onUpdateSource'];
};

function SourceEntry({
  source,
  index,
  onRemoveSource,
  onUpdateSource,
}: SourceEntryProps): ReactElement {
  const groupLabel = `Source ${index + 1}`;
  const valueId = useId();
  const noteId = useId();

  const showsUrlHint =
    URL_KINDS.has(source.kind) && source.value.length > 0 && !isPossiblyValidUrl(source.value);

  return (
    <div
      role="group"
      aria-label={groupLabel}
      className="rounded border border-[var(--rule)] p-4 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between">
        <Micro>{groupLabel}</Micro>
        <Button
          variant="ghost"
          type="button"
          onClick={() => onRemoveSource(source.draftId)}
        >
          Remove this source
        </Button>
      </div>

      <FormInput
        id={valueId}
        label={KIND_LABEL[source.kind]}
        placeholder={KIND_PLACEHOLDER[source.kind]}
        value={source.value}
        maxLength={SOURCE_VALUE_MAX_LENGTH}
        onChange={(event) => onUpdateSource(source.draftId, { value: event.target.value })}
      />

      {showsUrlHint && (
        <p className="text-[11px] text-[var(--warn)]">{MALFORMED_URL_HINT}</p>
      )}

      <FormInput
        id={noteId}
        label="Note"
        placeholder="Optional"
        value={source.note}
        maxLength={SOURCE_NOTE_MAX_LENGTH}
        onChange={(event) => onUpdateSource(source.draftId, { note: event.target.value })}
      />
    </div>
  );
}
